import { getDataReaderClient } from "@multiweb/clients/util";
import { IRowData, IUser } from "@multiweb/types/contracts";
import { OWObject } from "OW.Remoting/Remoting/DataTypes/OWObject";
import { AppContext } from "../../App";
import { CaseInsensitiveMap } from "../../Global/Base/DataTypes/CaseInsensitiveMap";
import { AttrValueReader } from "./AttrValueReader";
import { OWManager } from "./OWManager";

export type UserProfileManagerItemType = "Avatar" | string;

export class UserProfileManager {
    private static _userProfiles: CaseInsensitiveMap<UserProfileManagerItem> = new CaseInsensitiveMap<UserProfileManagerItem>();

    private static _getId(type: string, userId?: number): string {
        return `${type}|${userId ?? "#"}`;
    }

    public static async getUserProfileItem(userId: number | undefined, type: UserProfileManagerItemType, createIfNotExist: boolean = false): Promise<UserProfileManagerItem | undefined> {
        const key = UserProfileManager._getId(type, userId);
        if (UserProfileManager._userProfiles.has(key)) return UserProfileManager._userProfiles.get(key);

        const userProfiles = await getDataReaderClient()
            .getDataRowsWithParams("UserProfile",
                ["ID", "Name", "User", "Data", "DataType"],
                `${userId ? "User.ID=@1" : ":UserProfile.ActualUserProfiles()"} AND DataType = @0`,
                { params: [ type, userId ], dataLength: -1 },
            );

        let newItem: UserProfileManagerItem;

        if (userProfiles.length === 0) {
            if (createIfNotExist) {
                const newObj = await OWManager.newObject("UserProfile", undefined);

                await newObj.setValue("User", AppContext.instance.currentUser.id);
                await newObj.setValue("DataType", "Avatar");
                await newObj.applyUpdates();

                newItem = await UserProfileManagerItem.init(type, newObj);
            } else return undefined;
        } else newItem = await UserProfileManagerItem.init(type, userProfiles[0]);

        UserProfileManager._userProfiles.set(key, newItem);

        return newItem;
    }

    public static getActualUserProfileItem(type: UserProfileManagerItemType, createIfNotExist: boolean = false): Promise<UserProfileManagerItem | undefined> {
        return UserProfileManager.getUserProfileItem(undefined, type, createIfNotExist);
    }
}

export class UserProfileManagerItem {
    public readonly type: string;
    public readonly userInfo: IUser;
    private _object: OWObject;

    public get object(): OWObject {
        return this._object;
    }

    private constructor(type: UserProfileManagerItemType, userInfo: IUser) {
        this.type = type;
        this.userInfo = userInfo;
    }

    public static async init(type: UserProfileManagerItemType, data: IRowData | OWObject): Promise<UserProfileManagerItem> {
        const rUMI = new UserProfileManagerItem(type, AppContext.instance.currentUser);

        if (data instanceof OWObject) rUMI._object = data;
        else rUMI._object = await OWManager.getObject(AttrValueReader.read(data["ID"]), "UserProfile");

        return rUMI;
    }
}
